/**
 * WhatsApp Page — Inbox + Campanhas + Templates + Opt-Out
 * Módulo completo de WhatsApp integrado ao CRM
 * 
 * Regras anti-bloqueio implementadas:
 * - Indicador de risco em tempo real (🟢🟡🔴)
 * - Validação mínima 3 templates na criação de campanha
 * - Validação de links (proibido encurtados, máx 1 por mensagem)
 * - Janelas duplas de envio (09-11h / 14-17h)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import styles from './WhatsAppPage.module.css';

const API_BASE = '/api/whatsapp';

export function getAuthHeaders() {
    const token = localStorage.getItem('token');
    return {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
    };
}

const MIN_TEMPLATES = 3;

// =============================================
// COMPONENTE PRINCIPAL
// =============================================
export default function WhatsAppPage() {
    const [activeTab, setActiveTab] = useState('inbox');
    const [stats, setStats] = useState({});
    const [riskStatus, setRiskStatus] = useState(null);

    const fetchStats = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/inbox/stats`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setStats(data.data);
        } catch (e) { console.error('Erro ao buscar stats:', e); }
    }, []);

    const fetchRiskStatus = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/risk-status`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setRiskStatus(data.data);
        } catch (e) { console.error('Erro ao buscar risco:', e); }
    }, []);

    useEffect(() => {
        fetchStats();
        fetchRiskStatus();
        const iv = setInterval(() => { fetchStats(); fetchRiskStatus(); }, 30000);
        return () => clearInterval(iv);
    }, [fetchStats, fetchRiskStatus]);

    const tabs = [
        { id: 'inbox', label: '💬 Inbox', icon: '💬' },
        { id: 'campanhas', label: '📢 Campanhas', icon: '📢' },
        { id: 'templates', label: '📝 Templates', icon: '📝' },
        { id: 'optout', label: '🚫 Opt-Out / Logs', icon: '🚫' }
    ];

    const getRiskColor = () => {
        if (!riskStatus) return '#6c757d';
        switch (riskStatus.level) {
            case 'high': return '#dc3545';
            case 'elevated': return '#fd7e14';
            default: return '#28a745';
        }
    };

    const getRiskEmoji = () => {
        if (!riskStatus) return '⚪';
        switch (riskStatus.level) {
            case 'high': return '🔴';
            case 'elevated': return '🟡';
            default: return '🟢';
        }
    };

    const getRiskLabel = () => {
        if (!riskStatus) return 'Verificando...';
        switch (riskStatus.level) {
            case 'high': return 'Alto Risco — Envios Pausados';
            case 'elevated': return 'Risco Elevado — Volume Reduzido';
            default: return 'Normal';
        }
    };

    return (
        <div className={styles.whatsappContainer}>
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={styles.headerIcon}>📱</div>
                    <div>
                        <h1 className={styles.headerTitle}>WhatsApp</h1>
                        <p className={styles.headerSubtitle}>Inbox & Campanhas · Cloud API</p>
                    </div>
                </div>
                {/* Indicador de Risco */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    background: `${getRiskColor()}15`, border: `1px solid ${getRiskColor()}40`,
                    padding: '8px 16px', borderRadius: 10, fontSize: '0.85rem'
                }}>
                    <span style={{ fontSize: '1.2rem' }}>{getRiskEmoji()}</span>
                    <div>
                        <div style={{ fontWeight: 700, color: getRiskColor() }}>{getRiskLabel()}</div>
                        {riskStatus && (
                            <div style={{ fontSize: '0.75rem', color: '#6c757d', marginTop: 2 }}>
                                {riskStatus.dailyCount}/{riskStatus.effectiveLimit} msg ·
                                {riskStatus.isHardBlocked ? ' ⛔ Fora do horário' : ` 🕐 ${riskStatus.brasiliaHour}h BRT`}
                                {riskStatus.isPaused && ` · ⏸ Pausado até ${new Date(riskStatus.pausedUntil).toLocaleTimeString('pt-BR')}`}
                                {riskStatus.volumeReduction > 0 && ` · ↓${riskStatus.volumeReduction}% volume`}
                                {riskStatus.removeLinks && ' · 🚫 Links removidos'}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.statsBar}>
                <div className={`${styles.statCard} ${styles.statGreen}`}>
                    <div className={styles.statValue}>{stats.totalConversations || 0}</div>
                    <div className={styles.statLabel}>Conversas</div>
                </div>
                <div className={`${styles.statCard} ${styles.statBlue}`}>
                    <div className={styles.statValue}>{stats.unreadConversations || 0}</div>
                    <div className={styles.statLabel}>Não Lidas</div>
                </div>
                <div className={`${styles.statCard} ${styles.statOrange}`}>
                    <div className={styles.statValue}>
                        {riskStatus ? `${riskStatus.dailyCount}/${riskStatus.effectiveLimit}` : `${stats.dailySent || 0}/${stats.dailyLimit || 200}`}
                    </div>
                    <div className={styles.statLabel}>Enviadas Hoje</div>
                </div>
                <div className={`${styles.statCard} ${styles.statRed}`}>
                    <div className={styles.statValue}>{stats.optedOutContacts || 0}</div>
                    <div className={styles.statLabel}>Opt-Out</div>
                </div>
            </div>

            <div className={styles.tabsContainer}>
                <div className={styles.tabs}>
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`${styles.tab} ${activeTab === tab.id ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
                <div className={styles.tabContent}>
                    {activeTab === 'inbox' && <InboxTab onRefreshStats={fetchStats} />}
                    {activeTab === 'campanhas' && <CampanhasTab />}
                    {activeTab === 'templates' && <TemplatesTab />}
                    {activeTab === 'optout' && <OptOutTab />}
                </div>
            </div>
        </div>
    );
}

// =============================================
// INBOX TAB
// =============================================
export function InboxTab({ onRefreshStats }) {
    const [conversations, setConversations] = useState([]);
    const [selectedContact, setSelectedContact] = useState(null);
    const [messages, setMessages] = useState([]);
    const [contactInfo, setContactInfo] = useState(null);
    const [search, setSearch] = useState('');
    const [newMessage, setNewMessage] = useState('');
    const [sending, setSending] = useState(false);
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);

    const fetchConversations = useCallback(async () => {
        try {
            const params = new URLSearchParams({ limit: 100, ...(search && { search }) });
            const res = await fetch(`${API_BASE}/inbox?${params}`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setConversations(data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, [search]);

    useEffect(() => { fetchConversations(); }, [fetchConversations]);

    const fetchMessages = useCallback(async (contactId) => {
        try {
            const res = await fetch(`${API_BASE}/inbox/${contactId}/messages?limit=100`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) {
                setMessages(data.data);
                setContactInfo(data.contact);
            }
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => {
        if (selectedContact) {
            fetchMessages(selectedContact);
            const iv = setInterval(() => fetchMessages(selectedContact), 10000);
            return () => clearInterval(iv);
        }
    }, [selectedContact, fetchMessages]);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    const handleSend = async () => {
        if (!newMessage.trim() || !contactInfo) return;
        setSending(true);
        try {
            const res = await fetch(`${API_BASE}/send`, {
                method: 'POST',
                headers: getAuthHeaders(),
                body: JSON.stringify({ phone: contactInfo.phone, text: newMessage, contactName: contactInfo.name })
            });
            const data = await res.json();
            if (data.success) {
                setNewMessage('');
                fetchMessages(selectedContact);
                fetchConversations();
                onRefreshStats?.();
            } else {
                alert(data.message);
            }
        } catch (e) { alert('Erro ao enviar: ' + e.message); }
        finally { setSending(false); }
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr + 'Z');
        return d.toLocaleString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'sent': return <span className={`${styles.msgStatus} ${styles.msgStatusSent}`}>✓</span>;
            case 'delivered': return <span className={`${styles.msgStatus} ${styles.msgStatusDelivered}`}>✓✓</span>;
            case 'read': return <span className={`${styles.msgStatus} ${styles.msgStatusRead}`}>✓✓</span>;
            case 'failed': return <span className={`${styles.msgStatus} ${styles.msgStatusFailed}`}>✕</span>;
            default: return <span className={`${styles.msgStatus} ${styles.msgStatusSent}`}>⏳</span>;
        }
    };

    return (
        <div className={styles.inboxLayout}>
            <div className={styles.conversationsList}>
                <div className={styles.searchBox}>
                    <input
                        type="text"
                        placeholder="🔍 Buscar por nome ou telefone..."
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                    />
                </div>
                {loading ? (
                    <div className={styles.loading}>Carregando...</div>
                ) : conversations.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span>💬</span>
                        Nenhuma conversa ainda
                    </div>
                ) : conversations.map(conv => (
                    <div
                        key={conv.id}
                        className={`${styles.convItem} ${selectedContact === conv.contact_id ? styles.convItemActive : ''}`}
                        onClick={() => setSelectedContact(conv.contact_id)}
                    >
                        <div className={styles.convAvatar}>
                            {(conv.name || conv.phone_e164 || '?')[0].toUpperCase()}
                        </div>
                        <div className={styles.convInfo}>
                            <div className={styles.convName}>
                                {conv.name || conv.phone_e164}
                                {conv.opted_out ? <span className={styles.optOutBadge}> OPT-OUT</span> : ''}
                            </div>
                            <div className={styles.convPreview}>{conv.last_message_preview || 'Sem mensagens'}</div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                            <div className={styles.convTime}>{formatTime(conv.last_message_at)}</div>
                            {conv.unread_count > 0 && <div className={styles.convUnread}>{conv.unread_count}</div>}
                        </div>
                    </div>
                ))}
            </div>

            {selectedContact && contactInfo ? (
                <div className={styles.chatPanel}>
                    <div className={styles.chatHeader}>
                        <div className={styles.convAvatar}>
                            {(contactInfo.name || contactInfo.phone || '?')[0].toUpperCase()}
                        </div>
                        <div>
                            <div className={styles.chatHeaderName}>
                                {contactInfo.name || contactInfo.phone}
                                {contactInfo.optedOut && <span className={styles.optOutBadge}> OPT-OUT</span>}
                            </div>
                            <div className={styles.chatHeaderPhone}>{contactInfo.phone}</div>
                        </div>
                    </div>
                    <div className={styles.chatMessages}>
                        {messages.map(msg => (
                            <div key={msg.id} className={`${styles.msgBubble} ${msg.direction === 'out' ? styles.msgOut : styles.msgIn}`}>
                                <div>{msg.text}</div>
                                <div className={styles.msgTime}>
                                    {formatTime(msg.created_at)}
                                    {msg.direction === 'out' && getStatusIcon(msg.status)}
                                </div>
                            </div>
                        ))}
                        <div ref={messagesEndRef} />
                    </div>
                    <div className={styles.chatInput}>
                        <input
                            type="text"
                            placeholder={contactInfo.optedOut ? '🚫 Contato fez opt-out' : 'Digite sua mensagem...'}
                            value={newMessage}
                            onChange={e => setNewMessage(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && !e.shiftKey && handleSend()}
                            disabled={contactInfo.optedOut || sending}
                        />
                        <button
                            className={styles.sendBtn}
                            onClick={handleSend}
                            disabled={!newMessage.trim() || sending || contactInfo.optedOut}
                        >
                            ➤
                        </button>
                    </div>
                </div>
            ) : (
                <div className={styles.chatEmpty}>
                    <div>
                        <div style={{ fontSize: '3rem', marginBottom: 12 }}>💬</div>
                        <div>Selecione uma conversa para ver as mensagens</div>
                    </div>
                </div>
            )}
        </div>
    );
}

// =============================================
// CAMPANHAS TAB
// =============================================
export function CampanhasTab() {
    const [campaigns, setCampaigns] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [loading, setLoading] = useState(true);

    const fetchCampaigns = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/campaigns`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setCampaigns(data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchCampaigns(); }, [fetchCampaigns]);

    const handleAction = async (id, action) => {
        try {
            const res = await fetch(`${API_BASE}/campaigns/${id}/${action}`, {
                method: 'POST', headers: getAuthHeaders()
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                fetchCampaigns();
            } else {
                alert(data.message);
            }
        } catch (e) { alert('Erro: ' + e.message); }
    };

    const handleDelete = async (id) => {
        if (!confirm('Tem certeza que deseja deletar esta campanha?')) return;
        try {
            const res = await fetch(`${API_BASE}/campaigns/${id}`, {
                method: 'DELETE', headers: getAuthHeaders()
            });
            const data = await res.json();
            if (data.success) fetchCampaigns();
            else alert(data.message);
        } catch (e) { alert('Erro: ' + e.message); }
    };

    const getStatusClass = (status) => {
        const map = { draft: styles.statusDraft, running: styles.statusRunning, paused: styles.statusPaused, done: styles.statusDone, cancelled: styles.statusCancelled };
        return map[status] || styles.statusDraft;
    };

    const getStatusLabel = (status) => {
        const map = { draft: 'Rascunho', running: 'Enviando', paused: 'Pausada', done: 'Concluída', cancelled: 'Cancelada' };
        return map[status] || status;
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Campanhas</h3>
                <button className={styles.btnPrimary} onClick={() => setShowCreate(true)}>+ Nova Campanha</button>
            </div>

            {loading ? <div className={styles.loading}>Carregando...</div> :
                campaigns.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span>📢</span>
                        Nenhuma campanha criada ainda
                    </div>
                ) : (
                    <div className={styles.campaignGrid}>
                        {campaigns.map(camp => {
                            const progress = camp.total_recipients > 0 ? ((camp.sent_count || 0) / camp.total_recipients * 100) : 0;
                            return (
                                <div key={camp.id} className={styles.campaignCard}>
                                    <div className={styles.campaignHeader}>
                                        <span className={styles.campaignName}>{camp.name}</span>
                                        <span className={`${styles.campaignStatus} ${getStatusClass(camp.status)}`}>
                                            {getStatusLabel(camp.status)}
                                        </span>
                                    </div>
                                    {camp.description && <p style={{ fontSize: '0.85rem', color: '#6c757d', margin: '4px 0 0' }}>{camp.description}</p>}
                                    <div className={styles.progressBar}>
                                        <div className={styles.progressFill} style={{ width: `${progress}%` }} />
                                    </div>
                                    <div className={styles.campaignStats}>
                                        <span>📤 {camp.sent_count || 0} enviadas</span>
                                        <span>✅ {camp.delivered_count || 0} entregues</span>
                                        <span>👁 {camp.read_count || 0} lidas</span>
                                        <span>❌ {camp.failed_count || 0} falhas</span>
                                        <span>👥 {camp.total_recipients || 0} total</span>
                                    </div>
                                    <div className={styles.campaignActions}>
                                        {camp.status === 'draft' && (
                                            <button className={`${styles.btnPrimary} ${styles.btnSmall}`} onClick={() => handleAction(camp.id, 'start')}>
                                                ▶ Iniciar
                                            </button>
                                        )}
                                        {camp.status === 'running' && (
                                            <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => handleAction(camp.id, 'pause')}>
                                                ⏸ Pausar
                                            </button>
                                        )}
                                        {camp.status === 'paused' && (
                                            <button className={`${styles.btnPrimary} ${styles.btnSmall}`} onClick={() => handleAction(camp.id, 'resume')}>
                                                ▶ Retomar
                                            </button>
                                        )}
                                        {camp.status !== 'running' && (
                                            <button className={`${styles.btnDanger} ${styles.btnSmall}`} onClick={() => handleDelete(camp.id)}>
                                                🗑 Deletar
                                            </button>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

            {showCreate && <WACampaignModal onClose={() => setShowCreate(false)} onCreated={fetchCampaigns} />}
        </div>
    );
}

// =============================================
// CREATE CAMPAIGN MODAL
// =============================================
export function WACampaignModal({ onClose, onCreated }) {
    const [form, setForm] = useState({
        name: '', description: '', dailyLimit: 200, intervalMin: 30, intervalMax: 90,
        windowStart: '09:00-11:00,14:00-17:00', windowEnd: '17:00', isColdBase: false,
        templateIds: [], contactsText: ''
    });
    const [templates, setTemplates] = useState([]);
    const [saving, setSaving] = useState(false);
    const [linkWarnings, setLinkWarnings] = useState([]);

    useEffect(() => {
        fetch(`${API_BASE}/templates`, { headers: getAuthHeaders() })
            .then(r => r.json()).then(d => { if (d.success) setTemplates(d.data); });
    }, []);

    const handleSubmit = async () => {
        if (!form.name) return alert('Nome obrigatório');
        if (form.templateIds.length < MIN_TEMPLATES) {
            return alert(`Selecione pelo menos ${MIN_TEMPLATES} templates para rodízio anti-bloqueio (selecionados: ${form.templateIds.length})`);
        }

        // Parsear contatos (formato: telefone,nome — um por linha)
        const lines = form.contactsText.trim().split('\n').filter(l => l.trim());
        if (lines.length === 0) return alert('Adicione pelo menos 1 contato');

        const contacts = lines.map(line => {
            const parts = line.split(',');
            return { phone: parts[0]?.trim(), name: parts[1]?.trim() || '' };
        }).filter(c => c.phone);

        setSaving(true);
        try {
            const res = await fetch(`${API_BASE}/campaigns`, {
                method: 'POST', headers: getAuthHeaders(),
                body: JSON.stringify({ ...form, contacts })
            });
            const data = await res.json();
            if (data.success) {
                alert(data.message);
                onCreated();
                onClose();
            } else {
                if (data.linkWarnings) {
                    setLinkWarnings(data.linkWarnings);
                }
                alert(data.message);
            }
        } catch (e) { alert('Erro: ' + e.message); }
        finally { setSaving(false); }
    };

    const toggleTemplate = (id) => {
        setForm(prev => ({
            ...prev,
            templateIds: prev.templateIds.includes(id)
                ? prev.templateIds.filter(t => t !== id)
                : [...prev.templateIds, id]
        }));
    };

    const activeTemplates = templates.filter(t => t.active);
    const templateCountOk = form.templateIds.length >= MIN_TEMPLATES;

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>Nova Campanha</span>
                    <button className={styles.modalClose} onClick={onClose}>✕</button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.formGroup}>
                        <label>Nome da Campanha *</label>
                        <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Recuperação Clientes Inativos" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Descrição</label>
                        <input value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                    </div>

                    <div className={styles.formGroup}>
                        <label>
                            Templates (selecione mín. {MIN_TEMPLATES} para rodízio) *
                            <span style={{
                                marginLeft: 10, fontSize: '0.8rem', fontWeight: 600,
                                color: templateCountOk ? '#28a745' : '#dc3545'
                            }}>
                                {form.templateIds.length}/{MIN_TEMPLATES} selecionados {templateCountOk ? '✓' : '⚠'}
                            </span>
                        </label>
                        {activeTemplates.length < MIN_TEMPLATES ? (
                            <div style={{ fontSize: '0.85rem', color: '#dc3545', padding: '8px 12px', background: '#fff3f3', borderRadius: 6 }}>
                                ⚠️ Crie pelo menos {MIN_TEMPLATES} templates ativos na aba Templates antes de criar uma campanha.
                                Atualmente: {activeTemplates.length} ativo(s).
                            </div>
                        ) : (
                            activeTemplates.map(t => (
                                <label key={t.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: '0.88rem' }}>
                                    <input type="checkbox" checked={form.templateIds.includes(t.id)} onChange={() => toggleTemplate(t.id)} />
                                    <strong>{t.name}</strong> — <span style={{ color: '#6c757d' }}>{t.body.substring(0, 60)}...</span>
                                </label>
                            ))
                        )}
                    </div>

                    {linkWarnings.length > 0 && (
                        <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: 12, borderRadius: 8, fontSize: '0.82rem', marginBottom: 12 }}>
                            <strong>⚠️ Alertas de Links:</strong>
                            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                                {linkWarnings.map((w, i) => <li key={i}>{w}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Limite Diário</label>
                            <input type="number" value={form.dailyLimit} onChange={e => setForm(p => ({ ...p, dailyLimit: Number(e.target.value) }))} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>
                                <input type="checkbox" checked={form.isColdBase} onChange={e => setForm(p => ({ ...p, isColdBase: e.target.checked }))} style={{ width: 'auto', marginRight: 8 }} />
                                Base fria (reduz limite em 40%)
                            </label>
                        </div>
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Intervalo Mín (seg)</label>
                            <input type="number" value={form.intervalMin} onChange={e => setForm(p => ({ ...p, intervalMin: Number(e.target.value) }))} />
                        </div>
                        <div className={styles.formGroup}>
                            <label>Intervalo Máx (seg)</label>
                            <input type="number" value={form.intervalMax} onChange={e => setForm(p => ({ ...p, intervalMax: Number(e.target.value) }))} />
                        </div>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Janelas de Envio</label>
                        <input
                            value={form.windowStart}
                            onChange={e => setForm(p => ({ ...p, windowStart: e.target.value }))}
                            placeholder="09:00-11:00,14:00-17:00"
                        />
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                            Formato: HH:MM-HH:MM,HH:MM-HH:MM · Hard-block: nunca envia antes das 08:00 ou após 20:00 BRT
                        </span>
                    </div>

                    <div className={styles.formGroup}>
                        <label>Contatos (telefone,nome — um por linha) *</label>
                        <textarea
                            value={form.contactsText}
                            onChange={e => setForm(p => ({ ...p, contactsText: e.target.value }))}
                            placeholder={"5521999998888,João Silva\n5521988887777,Maria Santos\n5521977776666"}
                            rows={6}
                        />
                        <span style={{ fontSize: '0.75rem', color: '#6c757d' }}>
                            {form.contactsText.trim().split('\n').filter(l => l.trim()).length} contatos
                        </span>
                    </div>

                    {/* Anti-bloqueio info box */}
                    <div style={{ background: '#e8f4fd', padding: 14, borderRadius: 8, fontSize: '0.82rem', color: '#495057' }}>
                        <strong>🛡️ Regras Anti-Bloqueio Ativas:</strong>
                        <ul style={{ margin: '6px 0 0', paddingLeft: 16, lineHeight: 1.6 }}>
                            <li>Máximo 200 msg/dia (120 se base fria)</li>
                            <li>Cadência aleatória {form.intervalMin}-{form.intervalMax}s entre mensagens</li>
                            <li>Janelas de envio: {form.windowStart} · Hard-block 20h-08h BRT</li>
                            <li>Rodízio obrigatório de {MIN_TEMPLATES}+ templates</li>
                            <li>Proibido links encurtados · Máx 1 link/mensagem</li>
                            <li>Monitor de risco: pausa automática se alta taxa de falha ou opt-out</li>
                        </ul>
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnSecondary} onClick={onClose}>Cancelar</button>
                    <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving || !templateCountOk}>
                        {saving ? 'Criando...' : `Criar Campanha (${form.templateIds.length} templates)`}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================
// TEMPLATES TAB
// =============================================
export function TemplatesTab() {
    const [templates, setTemplates] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [editTemplate, setEditTemplate] = useState(null);
    const [loading, setLoading] = useState(true);

    const fetchTemplates = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/templates`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setTemplates(data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

    const handleDelete = async (id) => {
        if (!confirm('Deletar template?')) return;
        try {
            await fetch(`${API_BASE}/templates/${id}`, { method: 'DELETE', headers: getAuthHeaders() });
            fetchTemplates();
        } catch (e) { console.error(e); }
    };

    const handleToggle = async (id, currentActive) => {
        try {
            await fetch(`${API_BASE}/templates/${id}`, {
                method: 'PUT', headers: getAuthHeaders(),
                body: JSON.stringify({ active: !currentActive })
            });
            fetchTemplates();
        } catch (e) { console.error(e); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
                <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>Templates de Mensagem</h3>
                <button className={styles.btnPrimary} onClick={() => { setEditTemplate(null); setShowCreate(true); }}>+ Novo Template</button>
            </div>

            {templates.filter(t => t.active).length < MIN_TEMPLATES && (
                <div style={{ background: '#fff3cd', border: '1px solid #ffc107', padding: 12, borderRadius: 8, fontSize: '0.85rem', marginBottom: 16 }}>
                    ⚠️ <strong>Atenção:</strong> Você precisa de pelo menos <strong>{MIN_TEMPLATES} templates ativos</strong> para criar campanhas.
                    Atualmente: <strong>{templates.filter(t => t.active).length}</strong> ativo(s).
                </div>
            )}

            {loading ? <div className={styles.loading}>Carregando...</div> :
                templates.length === 0 ? (
                    <div className={styles.emptyState}>
                        <span>📝</span>
                        Nenhum template criado ainda
                    </div>
                ) : (
                    <div className={styles.templateGrid}>
                        {templates.map(t => (
                            <div key={t.id} className={styles.templateCard}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <div className={styles.templateName}>{t.name}</div>
                                    <span className={`${styles.activeBadge} ${t.active ? styles.activeBadgeOn : styles.activeBadgeOff}`}>
                                        {t.active ? 'Ativo' : 'Inativo'}
                                    </span>
                                </div>
                                <div className={styles.templateBody}>{t.body}</div>
                                <div className={styles.templateMeta}>
                                    <span>{t.category || 'MARKETING'}</span>
                                    <span>Usado {t.use_count || 0}x</span>
                                </div>
                                {t.meta_template_name && (
                                    <div style={{ fontSize: '0.75rem', color: '#128C7E', marginTop: 6 }}>
                                        Meta Template: {t.meta_template_name}
                                    </div>
                                )}
                                <div className={styles.templateActions}>
                                    <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => { setEditTemplate(t); setShowCreate(true); }}>
                                        ✏️ Editar
                                    </button>
                                    <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => handleToggle(t.id, t.active)}>
                                        {t.active ? '⏸ Desativar' : '▶ Ativar'}
                                    </button>
                                    <button className={`${styles.btnDanger} ${styles.btnSmall}`} onClick={() => handleDelete(t.id)}>
                                        🗑
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

            {showCreate && (
                <TemplateModal
                    template={editTemplate}
                    onClose={() => setShowCreate(false)}
                    onSaved={fetchTemplates}
                />
            )}
        </div>
    );
}

// =============================================
// TEMPLATE MODAL (com validação de links)
// =============================================
export function TemplateModal({ template, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: template?.name || '',
        body: template?.body || '',
        category: template?.category || 'MARKETING',
        metaTemplateName: template?.meta_template_name || ''
    });
    const [saving, setSaving] = useState(false);
    const [linkErrors, setLinkErrors] = useState([]);

    // Validar links em tempo real quando o body muda
    useEffect(() => {
        if (!form.body) { setLinkErrors([]); return; }
        const timer = setTimeout(async () => {
            try {
                const res = await fetch(`${API_BASE}/validate-links`, {
                    method: 'POST', headers: getAuthHeaders(),
                    body: JSON.stringify({ text: form.body })
                });
                const data = await res.json();
                if (data.success && data.data) {
                    setLinkErrors(data.data.errors || []);
                }
            } catch (e) { /* ignore */ }
        }, 500); // Debounce 500ms
        return () => clearTimeout(timer);
    }, [form.body]);

    const handleSubmit = async () => {
        if (!form.name || !form.body) return alert('Nome e corpo são obrigatórios');

        // Bloquear se tiver erros de links
        if (linkErrors.length > 0) {
            return alert(`Corrija os problemas nos links antes de salvar:\n${linkErrors.join('\n')}`);
        }

        setSaving(true);
        try {
            const url = template ? `${API_BASE}/templates/${template.id}` : `${API_BASE}/templates`;
            const method = template ? 'PUT' : 'POST';
            const res = await fetch(url, { method, headers: getAuthHeaders(), body: JSON.stringify(form) });
            const data = await res.json();
            if (data.success) { onSaved(); onClose(); }
            else alert(data.message);
        } catch (e) { alert('Erro: ' + e.message); }
        finally { setSaving(false); }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <div className={styles.modal} onClick={e => e.stopPropagation()}>
                <div className={styles.modalHeader}>
                    <span className={styles.modalTitle}>{template ? 'Editar Template' : 'Novo Template'}</span>
                    <button className={styles.modalClose} onClick={onClose}>✕</button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.formGroup}>
                        <label>Nome do Template *</label>
                        <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="Ex: Recuperação V1" />
                    </div>
                    <div className={styles.formGroup}>
                        <label>Corpo da Mensagem *</label>
                        <textarea
                            value={form.body}
                            onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                            placeholder="Olá! Notamos que faz tempo que você não compra conosco. Temos novidades incríveis..."
                            rows={5}
                            style={linkErrors.length > 0 ? { borderColor: '#dc3545' } : {}}
                        />
                    </div>

                    {/* Alertas de links em tempo real */}
                    {linkErrors.length > 0 && (
                        <div style={{
                            background: '#fff3f3', border: '1px solid #dc3545', padding: 10,
                            borderRadius: 6, fontSize: '0.82rem', color: '#dc3545', marginBottom: 8
                        }}>
                            <strong>⚠️ Problemas nos links:</strong>
                            <ul style={{ margin: '4px 0 0', paddingLeft: 16 }}>
                                {linkErrors.map((e, i) => <li key={i}>{e}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label>Categoria</label>
                            <select value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                                <option value="MARKETING">Marketing</option>
                                <option value="UTILITY">Utilitário</option>
                                <option value="AUTHENTICATION">Autenticação</option>
                            </select>
                        </div>
                        <div className={styles.formGroup}>
                            <label>Nome do Template no Meta (opcional)</label>
                            <input value={form.metaTemplateName} onChange={e => setForm(p => ({ ...p, metaTemplateName: e.target.value }))} placeholder="nome_template_meta" />
                        </div>
                    </div>

                    <div style={{ background: '#f0f8f0', padding: 14, borderRadius: 8, fontSize: '0.82rem', color: '#495057' }}>
                        <strong>📌 Dicas:</strong>
                        <ul style={{ margin: '4px 0 0', paddingLeft: 16, lineHeight: 1.6 }}>
                            <li>Se usar o "Nome do Template no Meta", o envio usará o template aprovado no Meta Business Suite</li>
                            <li>Máximo <strong>1 link</strong> por mensagem (preferencialmente seu próprio domínio)</li>
                            <li>Links encurtados (bit.ly, tinyurl, etc.) são <strong>proibidos</strong></li>
                            <li>Crie pelo menos <strong>{MIN_TEMPLATES} variações</strong> do template para rodízio anti-bloqueio</li>
                        </ul>
                    </div>
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnSecondary} onClick={onClose}>Cancelar</button>
                    <button className={styles.btnPrimary} onClick={handleSubmit} disabled={saving || linkErrors.length > 0}>
                        {saving ? 'Salvando...' : (template ? 'Salvar Alterações' : 'Criar Template')}
                    </button>
                </div>
            </div>
        </div>
    );
}

// =============================================
// OPT-OUT TAB
// =============================================
export function OptOutTab() {
    const [activeSubTab, setActiveSubTab] = useState('optout');
    const [optedOut, setOptedOut] = useState([]);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchOptedOut = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/optout`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setOptedOut(data.data);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);

    const fetchLogs = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE}/logs?limit=100`, { headers: getAuthHeaders() });
            const data = await res.json();
            if (data.success) setLogs(data.data);
        } catch (e) { console.error(e); }
    }, []);

    useEffect(() => { fetchOptedOut(); fetchLogs(); }, [fetchOptedOut, fetchLogs]);

    const handleRestore = async (id) => {
        if (!confirm('Remover opt-out deste contato?')) return;
        try {
            await fetch(`${API_BASE}/optout/${id}/restore`, { method: 'POST', headers: getAuthHeaders() });
            fetchOptedOut();
        } catch (e) { console.error(e); }
    };

    const formatDate = (d) => d ? new Date(d + 'Z').toLocaleString('pt-BR') : '-';

    return (
        <div>
            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
                <button
                    className={activeSubTab === 'optout' ? styles.btnPrimary : styles.btnSecondary}
                    onClick={() => setActiveSubTab('optout')}
                    style={{ fontSize: '0.85rem' }}
                >
                    🚫 Opt-Out ({optedOut.length})
                </button>
                <button
                    className={activeSubTab === 'logs' ? styles.btnPrimary : styles.btnSecondary}
                    onClick={() => setActiveSubTab('logs')}
                    style={{ fontSize: '0.85rem' }}
                >
                    📋 Logs de Falha ({logs.length})
                </button>
            </div>

            {loading ? <div className={styles.loading}>Carregando...</div> :
                activeSubTab === 'optout' ? (
                    optedOut.length === 0 ? (
                        <div className={styles.emptyState}>
                            <span>🚫</span>
                            Nenhum contato com opt-out
                        </div>
                    ) : (
                        <table className={styles.optoutTable}>
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Telefone</th>
                                    <th>Motivo</th>
                                    <th>Data</th>
                                    <th>Ação</th>
                                </tr>
                            </thead>
                            <tbody>
                                {optedOut.map(c => (
                                    <tr key={c.id}>
                                        <td>{c.name || '-'}</td>
                                        <td>{c.phone_e164}</td>
                                        <td>{c.opt_out_reason || '-'}</td>
                                        <td>{formatDate(c.opt_out_at)}</td>
                                        <td>
                                            <button className={`${styles.btnSecondary} ${styles.btnSmall}`} onClick={() => handleRestore(c.id)}>
                                                Restaurar
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                ) : (
                    logs.length === 0 ? (
                        <div className={styles.emptyState}>
                            <span>📋</span>
                            Nenhum log de falha
                        </div>
                    ) : (
                        <table className={styles.optoutTable}>
                            <thead>
                                <tr>
                                    <th>Job</th>
                                    <th>Telefone</th>
                                    <th>Erro</th>
                                    <th>Tentativas</th>
                                    <th>Status</th>
                                    <th>Data</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.map(l => (
                                    <tr key={l.id}>
                                        <td>#{l.id}</td>
                                        <td>{l.phone_e164 || '-'}</td>
                                        <td style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis' }}>{l.last_error || '-'}</td>
                                        <td>{l.attempts}/{l.max_attempts}</td>
                                        <td>
                                            <span style={{
                                                padding: '2px 8px', borderRadius: 4, fontSize: '0.78rem',
                                                background: l.status === 'failed' ? '#fee2e2' : '#fef3c7',
                                                color: l.status === 'failed' ? '#dc2626' : '#d97706'
                                            }}>
                                                {l.status}
                                            </span>
                                        </td>
                                        <td>{formatDate(l.created_at)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )
                )}
        </div>
    );
}
